<template>
  <div>
    <b-form>
      <b-form-group aria-describedby="supervisor-help-block" label="Supervisor"
        label-class="control-label workload-course-label"
        :class="{ 'invalid-group': $v.form.supervisorAsuriteId.$error }">
        <autocomplete :source="usersApi" :inputClass="{ 'form-control': true }" resultsValue="supervisorAsuriteId"
          :resultsDisplay="userDisplay" ref="userAutocomplete" @selected="selectUser" credentials="include"
          :request-headers="requestHeaders"></autocomplete>
        <span class="span-feedback invalid-feedback" v-if="$v.form.supervisorAsuriteId.$error">You must enter a
          name.</span>
      </b-form-group>

      <b-form-group label="Percent" label-class="control-label workload-course-label"
        :class="{ 'invalid-group': $v.form.administrativePercent.$error }">
        <b-form-input v-model="form.administrativePercent" placeholder="Enter numbers only"></b-form-input>
        <span class="span-feedback invalid-feedback" v-if="$v.form.administrativePercent.$error">You must enter a percent
          greater than 0 and less than your remianing
          administration percent ({{ this.percentLimit }}%).</span>
      </b-form-group>

      <b-form-group label="Do you have a course release?" label-class="control-label workload-course-label"
        v-slot="{ ariaDescribedby }" :class="{ 'invalid-group': $v.form.courseRelease.$error }">
        <b-form-radio-group id="courseRelease" v-model="form.courseRelease" :options="[
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ]" :aria-describedby="ariaDescribedby" name="courseRelease-radio"></b-form-radio-group>
        <span class="span-feedback invalid-feedback" v-if="$v.form.courseRelease.$error">Please select an option.</span>
      </b-form-group>

      <b-form-group label="Number of Courses" label-class="control-label workload-course-label"
        :class="{ 'invalid-group': $v.form.numberOfCourses.$error }">
        <b-form-input v-model="form.numberOfCourses" placeholder="Enter numbers only"
          :disabled="!form.courseRelease"></b-form-input>
        <span class="span-feedback invalid-feedback" v-if="$v.form.numberOfCourses.$error">You must enter the number of
          courses for your course releases.</span>
      </b-form-group>

      <b-form-group label="Do you have a stipend?" label-class="control-label workload-course-label"
        v-slot="{ ariaDescribedbyStipend }" :class="{ 'invalid-group': $v.form.stipend.$error }">
        <b-form-radio-group id="stipend" v-model="form.stipend" :options="[
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ]" :aria-describedby="ariaDescribedbyStipend" name="stipend-radio"></b-form-radio-group>
        <span class="span-feedback invalid-feedback" v-if="$v.form.stipend.$error">Please select an option.</span>
      </b-form-group>

      <b-form-group label="Explanation of Duties" label-class="control-label workload-course-label">
        <b-alert variant="info" show>Please indicate the title of your administrative role, including
          program (if relevant).</b-alert>
        <b-form-textarea :rows="3" placeholder="Enter Notes" v-model="form.notes"></b-form-textarea>
        <span v-if="$v.form.notes.$error" class="span-feedback invalid-feedback">
          Please enter a description of your administrative duties.
        </span>
      </b-form-group>

    </b-form>
  </div>
</template>

<script>
import * as _ from "lodash"; // eslint-disable-line no-unused-vars
import { required, decimal, between, integer } from "vuelidate/lib/validators";
import { VueEditor } from "vue2-editor";
import Autocomplete from "../../components/autocomplete";
import roleChecks from "../../misc/role-checks";

export default {
  mixins: [roleChecks],

  data() {
    return {
      statuses: [],
      form: {
        id: 0,
        workloadId: 0,
        notes: null,
        administrativePercent: 0.0,
        supervisorAsuriteId: null,
        courseRelease: null,
        numberOfCourses: null,
        stipend: null
      },
      basePercent: 0,
      baseUsedPercent: 0,
      requestHeaders: { "X-Requested-With": "XMLHttpRequest" }
    };
  },

  props: {
    value: {
      type: Boolean,
      required: false
    },
    adminRequest: {
      type: Object,
      required: false
    },
    totalPercent: {
      type: Number,
      required: false,
      default: function () {
        return 0;
      }
    },
    usedPercent: {
      type: Number,
      required: false,
      default: function () {
        return 0;
      }
    }
  },

  components: {
    VueEditor,
    Autocomplete
  },

  computed: {
    usersApi: function () {
      return process.env.VUE_APP_BASE_URI + "/api/employees/search?search=";
    },
    percentLimit: function () {
      if (this.adminRequest) {
        return this.basePercent + (this.totalPercent - this.baseUsedPercent);
      }
      return this.totalPercent - this.usedPercent;
    }
  },

  watch: {
    value: function () {
      if (this.value) {
        this.$v.form.$touch();
        if (!this.$v.form.$error) {
          this.$emit("complete", this.form);
          this.$v.$reset();
          this.form = {
            id: 0,
            workloadId: 0,
            notes: null,
            administrativePercent: 0.0,
            supervisorAsuriteId: null
          };
        } else {
          this.$emit("input", false);
        }
      }
    },

    editingAdminRequest: function () {
      if (this.editingAdminRequest) {
        this.setForm(this.editingAdminRequest);
      }
    },

    adminRequest: function () {
      if (this.adminRequest) {
        this.setForm(this.adminRequest);
      }
    }
  },

  validations() {
    var validations = {
      form: {
        notes: { required },
        administrativePercent: {
          required,
          decimal,
          between: between(0.01, this.percentLimit)
        },
        supervisorAsuriteId: { required },
        courseRelease: { required },
        numberOfCourses: {},
        stipend: { required }
      }
    };

    if (this.form.courseRelease) {
      validations.form.numberOfCourses = {
        required,
        integer
      };
    }
    return validations;
  },

  methods: {
    userDisplay: function (user) {
      return (
        user.firstName +
        " " +
        user.lastName +
        " (" +
        user.asuriteId +
        ") - " +
        (user.employeeJobs[0]
          ? user.employeeJobs[0].title +
          " - " +
          user.employeeJobs[0].department.description
          : "")
      );
    },
    selectUser: function (autoselectResponse) {
      this.form.supervisorAsuriteId =
        autoselectResponse.selectedObject.asuriteId;
      this.form.supervisor = autoselectResponse.selectedObject;
    },

    setForm: function (adminRequest) {
      // set values
      this.form = adminRequest;
      if (!this.form.administrativePercent) {
        this.form.administrativePercent = this.percentLimit;
      }
    }
  },

  mounted() {
    this.$refs.userAutocomplete.display =
      this.adminRequest && this.adminRequest.supervisor
        ? this.adminRequest.supervisor.fullName
        : "";
  },

  created() {
    if (this.adminRequest) {
      this.setForm(this.adminRequest);
      this.basePercent = this.adminRequest.administrativePercent;
      this.baseUsedPercent = this.usedPercent;
    } else {
      this.setForm(this.form);
    }
  }
};
</script>

<style scoped></style>
